import { CSSProperties, FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import PageBreadcrumbs from "../layouts/PageBreadcrumbs.js";
import { SkeletonTable } from "../components/Skeleton.js";
import {
  api,
  ApiError,
  DhisAppClient,
  DhisAppClientCreateRequest,
  DhisAppClientCreateResponse,
  loadSession
} from "../lib/api.js";
import { useTableState } from "../lib/useTableState.js";
import TableToolbar from "../components/TableToolbar.js";

type FlashKind = "ok" | "error" | "info";
type Flash = { kind: FlashKind; text: string } | null;

type EditorMode =
  | { kind: "closed" }
  | { kind: "create" }
  | { kind: "edit"; clientId: string };

type FormState = DhisAppClientCreateRequest & { allowedAuthoritiesText: string };

const EMPTY_FORM: FormState = {
  workspaceId: "",
  label: "",
  description: "",
  dhis2BaseUrl: "",
  allowedAuthorities: [],
  allowedAuthoritiesText: ""
};

const overlayStyle: CSSProperties = {
  position: "fixed", inset: 0, background: "rgba(33,41,52,0.5)",
  display: "grid", placeItems: "center", padding: 16, zIndex: 100
};
const modalStyle: CSSProperties = {
  width: "min(720px, 100%)", maxHeight: "90vh", overflow: "auto",
  background: "var(--surface)", border: "1px solid var(--border)"
};

function statusPill(status: DhisAppClient["status"]) {
  if (status === "revoked") return <span className="pill pill-err">Revoked</span>;
  return <span className="pill pill-ok">Active</span>;
}

function formatDate(value?: string): string {
  if (!value) return "—";
  try { return new Date(value).toLocaleString(); } catch { return value; }
}

export default function AppClients() {
  const session = loadSession();
  const isAdmin = session?.role === "admin";

  const [clients, setClients] = useState<DhisAppClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<Flash>(null);
  const [editor, setEditor] = useState<EditorMode>({ kind: "closed" });
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [busy, setBusy] = useState<string | null>(null);
  const [reveal, setReveal] = useState<{ clientId: string; clientSecret: string; label: string } | null>(null);

  const editing = useMemo(
    () => (editor.kind === "edit" ? clients.find((c) => c.clientId === editor.clientId) ?? null : null),
    [editor, clients]
  );

  const t = useTableState(clients as unknown as Array<Record<string, unknown>>, {
    searchFields: ["label", "clientId", "workspaceId", "dhis2BaseUrl", "status", "description"],
    initialPageSize: 25
  });

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const data = await api.listAppClients();
      setClients(data.clients ?? []);
    } catch (err) {
      setError(err instanceof ApiError ? `${err.message}${err.code ? ` (${err.code})` : ""}` : String(err));
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await refresh();
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [refresh]);

  useEffect(() => {
    if (!flash) return;
    const t = setTimeout(() => setFlash(null), 5000);
    return () => clearTimeout(t);
  }, [flash]);

  const onCreate = () => {
    setForm(EMPTY_FORM);
    setEditor({ kind: "create" });
  };

  const onEdit = (client: DhisAppClient) => {
    setForm({
      workspaceId: client.workspaceId,
      label: client.label,
      description: client.description ?? "",
      dhis2BaseUrl: client.dhis2BaseUrl ?? "",
      allowedAuthorities: client.allowedAuthorities,
      allowedAuthoritiesText: client.allowedAuthorities.join(", ")
    });
    setEditor({ kind: "edit", clientId: client.clientId });
  };

  const parseAuthorities = (text: string): string[] =>
    text.split(/[,\n]/).map((s) => s.trim()).filter(Boolean);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!isAdmin) return;
    const allowedAuthorities = parseAuthorities(form.allowedAuthoritiesText);
    setBusy("save");
    try {
      if (editor.kind === "create") {
        const res: DhisAppClientCreateResponse = await api.createAppClient({
          workspaceId: form.workspaceId,
          label: form.label,
          description: form.description || undefined,
          dhis2BaseUrl: form.dhis2BaseUrl || undefined,
          allowedAuthorities
        });
        setEditor({ kind: "closed" });
        setReveal({ clientId: res.client.clientId, clientSecret: res.clientSecret, label: res.client.label });
        setFlash({ kind: "ok", text: `Client ${res.client.clientId} created. Copy the secret now — it is shown only once.` });
      } else if (editor.kind === "edit") {
        await api.updateAppClient(editor.clientId, {
          label: form.label,
          description: form.description || undefined,
          dhis2BaseUrl: form.dhis2BaseUrl || undefined,
          allowedAuthorities
        });
        setEditor({ kind: "closed" });
        setFlash({ kind: "ok", text: "Client updated." });
      }
      await refresh();
    } catch (err) {
      setFlash({ kind: "error", text: err instanceof Error ? err.message : String(err) });
    } finally {
      setBusy(null);
    }
  };

  const onRotate = async (client: DhisAppClient) => {
    if (!window.confirm(`Rotate the client secret for "${client.label}"? The previous secret will stop working immediately.`)) return;
    setBusy(`rotate:${client.clientId}`);
    try {
      const res = await api.rotateAppClientSecret(client.clientId);
      setReveal({ clientId: res.client.clientId, clientSecret: res.clientSecret, label: res.client.label });
      setFlash({ kind: "ok", text: "Secret rotated. Copy the new secret now — it is shown only once." });
      await refresh();
    } catch (err) {
      setFlash({ kind: "error", text: err instanceof Error ? err.message : String(err) });
    } finally {
      setBusy(null);
    }
  };

  const onRevoke = async (client: DhisAppClient) => {
    if (!window.confirm(`Revoke access for "${client.label}"? Apps using this client will no longer be able to authenticate.`)) return;
    setBusy(`revoke:${client.clientId}`);
    try {
      await api.revokeAppClient(client.clientId);
      setFlash({ kind: "info", text: "Client revoked." });
      await refresh();
    } catch (err) {
      setFlash({ kind: "error", text: err instanceof Error ? err.message : String(err) });
    } finally {
      setBusy(null);
    }
  };

  // v1.17.0 — workspace admin queues a full historical backfill for
  // this client. The DHIS2 app picks it up on its next heartbeat (≤60s).
  const onTriggerBackfill = async (client: DhisAppClient) => {
    if (!window.confirm(
      `Trigger a full historical backfill on "${client.label}"?\n\n`
      + `On the next heartbeat (within 60s), the DHIS2 app will fetch every metadata `
      + `object, every tracker event, and every aggregate data value from the paired DHIS2 `
      + `instance and push it to the platform. Existing platform data is not deleted; ingest `
      + `is idempotent so duplicates are de-duped server-side.`
    )) return;
    setBusy(`backfill:${client.clientId}`);
    try {
      await api.triggerAppClientBackfill(client.clientId, "Operator-triggered from /app/administration/connections/app-clients");
      setFlash({ kind: "info", text: "Backfill command queued. The DHIS2 app will start the backfill on its next heartbeat (within 60s)." });
    } catch (err) {
      setFlash({ kind: "error", text: err instanceof Error ? err.message : String(err) });
    } finally {
      setBusy(null);
    }
  };

  const onReactivate = async (client: DhisAppClient) => {
    setBusy(`reactivate:${client.clientId}`);
    try {
      await api.updateAppClient(client.clientId, { status: "active" });
      setFlash({ kind: "ok", text: "Client reactivated." });
      await refresh();
    } catch (err) {
      setFlash({ kind: "error", text: err instanceof Error ? err.message : String(err) });
    } finally {
      setBusy(null);
    }
  };

  const onDelete = async (client: DhisAppClient) => {
    if (!window.confirm(`Delete "${client.label}" permanently? This cannot be undone.`)) return;
    setBusy(`delete:${client.clientId}`);
    try {
      await api.deleteAppClient(client.clientId);
      setFlash({ kind: "info", text: "Client deleted." });
      await refresh();
    } catch (err) {
      setFlash({ kind: "error", text: err instanceof Error ? err.message : String(err) });
    } finally {
      setBusy(null);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setFlash({ kind: "ok", text: "Copied to clipboard." });
    } catch {
      setFlash({ kind: "error", text: "Copy failed. Select the value and press Ctrl/Cmd-C." });
    }
  };

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>DHIS2 app clients</h1>
          <p>Platform-issued credentials that a DHIS2 instance&rsquo;s embedded TallyStream app uses to authenticate with this backend. Clients are auto-provisioned from the DHIS2 instances page by discovering the installed TallyStream app — they are not created manually.</p>
        </div>
        <div className="actions">
          <button className="btn" type="button" onClick={() => void refresh()}>Refresh</button>
        </div>
      </header>

      <div className="alert alert-info">
        To issue new client credentials, go to <strong>DHIS2 instances</strong>, store the DHIS2 credential on the instance, then click <strong>Discover app</strong>. The platform will detect the installed TallyStream app and generate a one-shot client secret for you to paste into the DHIS2 app&rsquo;s setup form.
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {flash && <div className={`alert alert-${flash.kind}`}>{flash.text}</div>}

      {!isAdmin && (
        <div className="alert alert-info">
          App client management is restricted to administrators. Sign in with an admin account to manage clients.
        </div>
      )}

      {loading ? (
        <SkeletonTable rows={5} columns={4} />
      ) : clients.length === 0 ? (
        <div className="empty">
          No DHIS2 app clients yet. Register a DHIS2 instance and run <strong>Discover app</strong> to auto-provision one.
        </div>
      ) : (
        <div className="table-scroll">
          <TableToolbar
            search={t.search} setSearch={t.setSearch}
            page={t.page} setPage={t.setPage}
            pageSize={t.pageSize} setPageSize={t.setPageSize}
            totalPages={t.totalPages} totalRows={t.totalRows}
            hideExport
            searchPlaceholder="Search clients…"
          />
          <table className="data-table">
            <thead>
              <tr>
                <th>Label</th>
                <th>Client ID</th>
                <th>Workspace</th>
                <th>DHIS2 base URL</th>
                <th>Status</th>
                <th>Authorities</th>
                <th>Last used</th>
                <th>Secret rotated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {(t.paged as unknown as DhisAppClient[]).map((c) => (
                <tr key={c.clientId}>
                  <td>
                    <strong>{c.label}</strong>
                    {c.description && <div className="soft" style={{ fontSize: 12, marginTop: 2 }}>{c.description}</div>}
                  </td>
                  <td><code>{c.clientId}</code></td>
                  <td><code>{c.workspaceId}</code></td>
                  <td className="soft">{c.dhis2BaseUrl ?? "—"}</td>
                  <td>{statusPill(c.status)}</td>
                  <td className="soft">
                    {c.allowedAuthorities.length === 0 ? (
                      <span className="pill pill-muted">inherit</span>
                    ) : (
                      c.allowedAuthorities.map((a) => (
                        <span key={a} className="pill pill-muted" style={{ marginRight: 4 }}>{a}</span>
                      ))
                    )}
                  </td>
                  <td className="soft">{formatDate(c.lastUsedAt)}</td>
                  <td className="soft">{formatDate(c.secretLastRotatedAt)}</td>
                  <td>
                    <div className="row-wrap" style={{ gap: 4 }}>
                      <button className="btn btn-sm" type="button" onClick={() => onEdit(c)} disabled={!isAdmin}>Edit</button>
                      <button
                        className="btn btn-sm"
                        type="button"
                        onClick={() => void onTriggerBackfill(c)}
                        disabled={!isAdmin || busy === `backfill:${c.clientId}` || c.status !== "active"}
                        title="Queue a full historical metadata + data backfill. The DHIS2 app picks it up on its next heartbeat (≤60s)."
                      >
                        Trigger backfill
                      </button>
                      <button className="btn btn-sm" type="button" onClick={() => void onRotate(c)} disabled={!isAdmin || busy === `rotate:${c.clientId}`}>Rotate</button>
                      {c.status === "active" ? (
                        <button className="btn btn-sm btn-danger" type="button" onClick={() => void onRevoke(c)} disabled={!isAdmin || busy === `revoke:${c.clientId}`}>Revoke</button>
                      ) : (
                        <button className="btn btn-sm" type="button" onClick={() => void onReactivate(c)} disabled={!isAdmin || busy === `reactivate:${c.clientId}`}>Reactivate</button>
                      )}
                      <button className="btn btn-sm btn-danger" type="button" onClick={() => void onDelete(c)} disabled={!isAdmin || busy === `delete:${c.clientId}`}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editor.kind !== "closed" && (
        <div style={overlayStyle}>
          <form className="card" style={modalStyle} onSubmit={onSubmit}>
            <div className="card-head">
              <h2>{editor.kind === "create" ? "Register DHIS2 app client" : `Edit ${editing?.label ?? ""}`}</h2>
              <button type="button" className="linkbtn" onClick={() => setEditor({ kind: "closed" })}>Close</button>
            </div>
            <div className="card-body">
              <div className="form-grid">
                <div className="field">
                  <label>Label <span style={{ color: "var(--err)" }}>*</span></label>
                  <input
                    value={form.label}
                    onChange={(e) => setForm({ ...form, label: e.target.value })}
                    required
                    placeholder="Uganda MoH — Production DHIS2"
                  />
                </div>
                <div className="field">
                  <label>Workspace ID <span style={{ color: "var(--err)" }}>*</span></label>
                  <input
                    value={form.workspaceId}
                    onChange={(e) => setForm({ ...form, workspaceId: e.target.value })}
                    required
                    disabled={editor.kind === "edit"}
                    placeholder="ws-ministry-uganda"
                  />
                  <small>Tenant workspace this client is scoped to.</small>
                </div>
                <div className="field" style={{ gridColumn: "1 / -1" }}>
                  <label>Description</label>
                  <input
                    value={form.description ?? ""}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="Optional — purpose, owning team, DHIS2 server contact"
                  />
                </div>
                <div className="field" style={{ gridColumn: "1 / -1" }}>
                  <label>DHIS2 base URL</label>
                  <input
                    type="url"
                    value={form.dhis2BaseUrl ?? ""}
                    onChange={(e) => setForm({ ...form, dhis2BaseUrl: e.target.value })}
                    placeholder="https://dhis2.health.go.ug"
                  />
                  <small>Informational hint for the DHIS2 app; not enforced by the platform.</small>
                </div>
                <div className="field" style={{ gridColumn: "1 / -1" }}>
                  <label>Allowed DHIS2 authorities</label>
                  <textarea
                    rows={3}
                    value={form.allowedAuthoritiesText}
                    onChange={(e) => setForm({ ...form, allowedAuthoritiesText: e.target.value })}
                    placeholder="F_TALLYSTREAM_ADMIN, F_TALLYSTREAM_ANALYST"
                  />
                  <small>Comma- or newline-separated. If set, only these authorities from the DHIS2 user are honoured. Leave empty to accept whatever the DHIS2 app presents.</small>
                </div>
              </div>
              <div className="alert alert-info">
                {editor.kind === "create"
                  ? "On save, the platform will generate a client ID and a one-shot client secret for you to copy into the DHIS2 app's setup form."
                  : "Credentials are managed separately. Use Rotate to issue a new secret; the old secret is invalidated immediately."}
              </div>
            </div>
            <div className="card-head" style={{ borderTop: "1px solid var(--divider)", borderBottom: 0, justifyContent: "flex-end" }}>
              <button type="button" className="btn" onClick={() => setEditor({ kind: "closed" })} disabled={busy === "save"}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={busy === "save"}>
                {busy === "save" ? "Saving…" : editor.kind === "create" ? "Register client" : "Save changes"}
              </button>
            </div>
          </form>
        </div>
      )}

      {reveal && (
        <div style={overlayStyle}>
          <div className="card" style={{ ...modalStyle, maxWidth: 560 }}>
            <div className="card-head">
              <h2>Client secret — copy now</h2>
            </div>
            <div className="card-body">
              <div className="alert alert-warn">
                This secret is shown only once. Paste it into the DHIS2 app&rsquo;s setup form or your secret manager before closing this dialog.
              </div>
              <dl className="dl-kv">
                <dt>Label</dt><dd>{reveal.label}</dd>
                <dt>Client ID</dt>
                <dd>
                  <code>{reveal.clientId}</code>{" "}
                  <button className="btn btn-sm" type="button" onClick={() => void copyToClipboard(reveal.clientId)}>Copy</button>
                </dd>
                <dt>Client secret</dt>
                <dd>
                  <code style={{ wordBreak: "break-all" }}>{reveal.clientSecret}</code>{" "}
                  <button className="btn btn-sm" type="button" onClick={() => void copyToClipboard(reveal.clientSecret)}>Copy</button>
                </dd>
              </dl>
            </div>
            <div className="card-head" style={{ borderTop: "1px solid var(--divider)", borderBottom: 0, justifyContent: "flex-end" }}>
              <button type="button" className="btn btn-primary" onClick={() => setReveal(null)}>Done — I&rsquo;ve copied the secret</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
